<template>
  <div class="question-container">
    <!-- 顶部横幅 -->
    <div class="page-header">
      <TopHeader :fixed="false" />
      <!-- 工具栏 -->
      <div class="toolbar">
        <div class="back-link" @click="goBack">
          <img src="@/assets/image/back.png" class="back-icon" />
          <span>退出答题</span>
        </div>
        <div class="title">专项练习 - {{ sectionName }}</div>
        <div class="toolbar-right">
          <button class="redo-btn" @click="showRedoModal = true">重做</button>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-wrapper">
      <div class="content-card">
        <!-- 加载中 -->
        <div v-if="loading" class="loading-box">加载中...</div>
        
        <!-- 无题目 -->
        <div v-else-if="questions.length === 0" class="empty-box">暂无题目</div>

        <!-- 题目列表 -->
        <div v-else class="question-list">
          <div 
            v-for="(q, index) in questions" 
            :key="index" 
            :id="'question-' + index"
            class="question-item"
          >
            <div class="question-header">
              <span class="question-title">{{ index + 1 }}.{{ q.title }}</span>
              <div class="question-right">
                <span :class="['question-type', q.type === 'single' ? 'single' : 'multi']">
                  {{ q.type === 'single' ? '单选题' : '多选题' }}
                </span>
                <img 
                  :src="q.favorited ? require('@/assets/image/star-select.png') : require('@/assets/image/star.png')" 
                  class="favorite-icon"
                  @click="toggleFavorite(index)"
                />
              </div>
            </div>
            <div class="options">
              <div 
                v-for="(opt, optIndex) in q.options" 
                :key="optIndex"
                :class="getOptionClass(index, optIndex)"
                @click="selectOption(index, optIndex)"
              >
                <span :class="['option-label', q.type === 'single' ? 'circle' : 'square']">{{ ['A', 'B', 'C', 'D', 'E', 'F'][optIndex] }}</span>
                <span v-if="opt.content" class="option-text">{{ opt.content }}</span>
                <img v-else-if="opt.image" :src="baseURL + opt.image" class="option-image" @click.stop="showPreview(baseURL + opt.image)" />
              </div>
            </div>
            <!-- 多选确认按钮 -->
            <div v-if="q.type === 'multi' && !q.answered && q.selected.length > 0" class="confirm-multi">
              <button class="confirm-btn" @click="confirmMulti(index)">确认答案</button>
            </div>
            <!-- 答案解析 -->
            <div v-if="q.answered" class="answer-section">
              <div class="answer-info">
                <span class="answer-label">正确答案：{{ getAnswerText(q.answer) }}</span>
                <span v-if="q.selected.length > 0" class="your-answer">你的答案：{{ getAnswerText(q.selected) }}</span>
              </div>
              <div class="answer-explain">
                {{ q.analysis }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 回到顶部 -->
      <div class="back-top" @click="scrollToTop">
        <img src="@/assets/image/up.png" class="arrow-icon" />
        <span>顶部</span>
      </div>
    </div>

    <!-- 底部答题卡 -->
    <div :class="['footer-bar', { expanded: showAnswerCard }]">
      <div class="footer-content">
        <div class="answer-card-toggle" @click="showAnswerCard = !showAnswerCard">
          <img :src="showAnswerCard ? require('@/assets/image/down.png') : require('@/assets/image/up.png')" class="arrow-icon" />
          <span>答题卡</span>
        </div>
        <div v-if="showAnswerCard" class="answer-stats">
          <span class="stat-item correct">
            <span class="stat-dot"></span>
            做对（{{ correctCount }}）
          </span>
          <span class="stat-item wrong">
            <span class="stat-dot"></span>
            做错（{{ wrongCount }}）
          </span>
          <span class="stat-item unanswered">
            <span class="stat-dot"></span>
            未做题（{{ unansweredCount }}）
          </span>
        </div>
        <div v-else class="pagination-row">
          <span 
            v-for="(q, index) in questions" 
            :key="index"
            :class="['page-num', getPageNumClass(index)]"
            @click="scrollToQuestion(index)"
          >{{ index + 1 }}</span>
        </div>
      </div>
      <div v-if="showAnswerCard" class="answer-card-grid">
        <span 
          v-for="(q, index) in questions" 
          :key="index"
          :class="['grid-num', getPageNumClass(index)]"
          @click="scrollToQuestion(index)"
        >{{ index + 1 }}</span>
      </div>
    </div>

    <!-- 重做弹窗 -->
    <div v-if="showRedoModal" class="modal-overlay" @click.self="showRedoModal = false">
      <div class="modal-box">
        <div class="modal-content">
          确定要重做吗？将清除本节所有做题记录
        </div>
        <div class="modal-buttons">
          <button class="modal-btn confirm" @click="confirmRedo">确定</button>
          <button class="modal-btn cancel" @click="showRedoModal = false">取消</button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="previewImage = ''">
      <img :src="previewImage" class="preview-img" @click.stop />
    </div>
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import { getQuestionList, submitAnswer, toggleFavorite as toggleFavoriteApi, checkFavorites, resetSectionRecord } from '@/api/question'
import { decodeId } from '@/utils/idEncoder'
import config from '@/utils/config'

export default {
  name: 'QuestionPage',
  components: {
    TopHeader
  },
  data() {
    return {
      baseURL: config.imageBaseURL,
      sectionId: null,
      mode: '1',
      sectionName: '',
      showRedoModal: false,
      showAnswerCard: false,
      loading: true,
      questions: [],
      previewImage: ''
    }
  },
  computed: {
    unfinishedCount() {
      return this.questions.filter(q => !q.answered).length
    },
    correctCount() {
      return this.questions.filter(q => {
        if (!q.answered) return false
        return JSON.stringify([...q.selected].sort()) === JSON.stringify([...q.answer].sort())
      }).length
    },
    wrongCount() {
      return this.questions.filter(q => {
        if (!q.answered) return false
        return JSON.stringify([...q.selected].sort()) !== JSON.stringify([...q.answer].sort())
      }).length
    },
    unansweredCount() {
      return this.questions.filter(q => !q.answered).length
    }
  },
  created() {
    this.sectionId = decodeId(this.$route.params.sectionId)
    this.mode = this.$route.query.mode || '1'
    this.loadQuestions()
  },
  methods: {
    async loadQuestions() {
      this.loading = true
      try {
        const res = await getQuestionList(this.sectionId, this.mode)
        this.sectionName = res.data.sectionName || ''
        const list = res.data.questions || []
        // 转换为页面需要的格式
        this.questions = list.map(q => {
          const item = {
            questionId: q.questionId,
            title: q.title,
            type: q.type,
            options: q.options,
            answer: [],
            analysis: '',
            selected: [],
            answered: false,
            favorited: false,
            startTime: Date.now()
          }
          // 如果已答过，恢复答题状态
          if (q.answered) {
            item.answered = true
            item.analysis = q.analysis || '暂无解析'
            // 将用户答案转换为索引数组
            if (q.userAnswer) {
              item.selected = q.userAnswer.split(',').map(label => label.charCodeAt(0) - 65)
            }
            // 将正确答案转换为索引数组
            if (q.correctAnswer) {
              item.answer = q.correctAnswer.split(',').map(label => label.charCodeAt(0) - 65)
            }
          }
          return item
        })
        // 加载收藏状态
        await this.loadFavoriteStatus()
      } catch (error) {
        console.error('加载题目失败', error)
      } finally {
        this.loading = false
      }
    },
    async loadFavoriteStatus() {
      if (this.questions.length === 0) return
      try {
        const questionIds = this.questions.map(q => q.questionId)
        const res = await checkFavorites(questionIds)
        const favoriteIds = res.data || []
        this.questions.forEach(q => {
          q.favorited = favoriteIds.includes(q.questionId)
        })
      } catch (error) {
        console.error('加载收藏状态失败', error)
      }
    },
    goBack() {
      this.$router.push('/home')
    },
    async confirmRedo() {
      this.showRedoModal = false
      try {
        await resetSectionRecord(this.sectionId)
        // 重新加载题目
        this.loadQuestions()
      } catch (error) {
        console.error('重做失败', error)
      }
    },
    async selectOption(qIndex, optIndex) {
      const q = this.questions[qIndex]
      if (q.answered) return
      
      if (q.type === 'single') {
        q.selected = [optIndex]
        // 提交答案并获取结果
        await this.submitAndCheck(qIndex)
      } else {
        const idx = q.selected.indexOf(optIndex)
        if (idx > -1) {
          q.selected.splice(idx, 1)
        } else {
          q.selected.push(optIndex)
        }
      }
    },
    async confirmMulti(qIndex) {
      const q = this.questions[qIndex]
      if (q.selected.length > 0) {
        await this.submitAndCheck(qIndex)
      }
    },
    async submitAndCheck(qIndex) {
      const q = this.questions[qIndex]
      const answerTime = Math.floor((Date.now() - q.startTime) / 1000)
      const userAnswer = q.selected.map(i => ['A', 'B', 'C', 'D', 'E', 'F'][i]).join(',')
      
      try {
        const res = await submitAnswer({
          questionId: q.questionId,
          sectionId: this.sectionId,
          userAnswer: userAnswer,
          answerTime: answerTime,
          mode: this.mode
        })
        
        // 更新题目状态
        q.answered = true
        q.analysis = res.data.analysis || '暂无解析'
        // 将正确答案转换为索引数组
        const correctLabels = res.data.correctAnswer.split(',')
        q.answer = correctLabels.map(label => label.charCodeAt(0) - 65)
        
      } catch (error) {
        console.error('提交答案失败', error)
      }
    },
    getOptionClass(qIndex, optIndex) {
      const q = this.questions[qIndex]
      const isSelected = q.selected.includes(optIndex)
      const isCorrect = q.answer.includes(optIndex)
      
      if (!q.answered) {
        return isSelected ? ['option', 'selected'] : ['option']
      }
      
      if (isCorrect) {
        return ['option', 'correct']
      }
      if (isSelected && !isCorrect) {
        return ['option', 'wrong']
      }
      return ['option']
    },
    getPageNumClass(index) {
      const q = this.questions[index]
      if (!q.answered) return ''
      const isCorrect = JSON.stringify([...q.selected].sort()) === JSON.stringify([...q.answer].sort())
      return isCorrect ? 'correct' : 'wrong'
    },
    getAnswerText(arr) {
      return arr.map(i => ['A', 'B', 'C', 'D', 'E', 'F'][i]).join('、')
    },
    async toggleFavorite(index) {
      const q = this.questions[index]
      try {
        const res = await toggleFavoriteApi(q.questionId)
        q.favorited = res.data
      } catch (error) {
        console.error('收藏操作失败', error)
      }
    },
    scrollToTop() {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    },
    scrollToQuestion(index) {
      const el = document.getElementById('question-' + index)
      if (el) {
        const headerHeight = 130
        const top = el.offsetTop - headerHeight
        document.documentElement.scrollTop = top
        document.body.scrollTop = top
      }
    },
    showPreview(url) {
      this.previewImage = url
    }
  }
}
</script>

<style scoped>
.question-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
  padding-top: 120px;
}

.page-header {
  background: #fff;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.page-header :deep(.header) {
  box-shadow: none;
}

.toolbar {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 30px;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.main-wrapper {
  max-width: 900px;
  margin: 30px auto;
  padding: 0 20px;
}

.content-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

.loading-box, .empty-box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #999;
  font-size: 14px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333;
  cursor: pointer;
  font-size: 14px;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.back-link:hover {
  color: #3d8c40;
}

.title {
  font-size: 15px;
  color: #333;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.redo-btn {
  padding: 8px 24px;
  background: #fff;
  color: #3d8c40;
  border: 1px solid #3d8c40;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.redo-btn:hover {
  background: #f0f7f0;
}

.timer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.timer-icon {
  width: 16px;
  height: 16px;
}

.question-list {
  padding: 0 25px;
}

.question-item {
  padding: 25px 0;
  border-bottom: 1px solid #f0f0f0;
}

.question-item:last-child {
  border-bottom: none;
}

.question-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 18px;
}

.question-title {
  flex: 1;
  font-size: 15px;
  color: #333;
  line-height: 1.6;
}

.question-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.question-type {
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}

.question-type.single {
  color: #3d8c40;
}

.question-type.multi {
  color: #e74c3c;
}

.favorite-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
  border: 1px solid #e8e8e8;
  min-height: 54px;
  box-sizing: border-box;
}

.option:hover {
  background: #f8f8f8;
}

.option.selected {
  border-color: #3d8c40;
  background: #f0f7f0;
}

.option.selected .option-label {
  background: #3d8c40;
  color: #fff;
  border-color: #3d8c40;
}

.option.correct {
  background: #d4edda;
  border-color: #3d8c40;
}

.option.correct .option-label {
  background: #3d8c40;
  color: #fff;
  border-color: #3d8c40;
}

.option.wrong {
  background: #f8d7da;
  border-color: #e74c3c;
}

.option.wrong .option-label {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}

.option-label {
  width: 26px;
  height: 26px;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.option-label.circle {
  border-radius: 50%;
}

.option-label.square {
  border-radius: 4px;
}

.option-text {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.option-image {
  height: 40px;
  width: auto;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.preview-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
}

.confirm-multi {
  margin-top: 15px;
  text-align: right;
}

.confirm-btn {
  padding: 8px 20px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn:hover {
  background: #2e7d32;
}

.answer-section {
  margin-top: 20px;
  padding: 15px;
  background: #fafafa;
  border-left: 3px solid #3d8c40;
  border-radius: 0 6px 6px 0;
}

.answer-info {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  font-size: 13px;
}

.answer-label {
  color: #3d8c40;
}

.your-answer {
  color: #666;
}

.answer-explain {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
}

.back-top {
  position: fixed;
  right: 30px;
  bottom: 100px;
  padding: 10px 15px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.arrow-icon {
  width: 12px;
  height: 12px;
}

.back-top:hover {
  border-color: #3d8c40;
  color: #3d8c40;
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  transition: all 0.3s;
}

.footer-bar.expanded {
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
}

.footer-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 15px 25px;
  display: flex;
  align-items: center;
  gap: 25px;
}

.answer-card-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.answer-stats {
  display: flex;
  align-items: center;
  gap: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.stat-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.stat-item.correct .stat-dot {
  background: #3d8c40;
}

.stat-item.wrong .stat-dot {
  background: #e74c3c;
}

.stat-item.unanswered .stat-dot {
  background: #fff;
  border: 2px solid #ddd;
}

.answer-card-grid {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 25px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.grid-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.grid-num:hover {
  background: #e8e8e8;
}

.grid-num.correct {
  background: #3d8c40;
  color: #fff;
}

.grid-num.wrong {
  background: #e74c3c;
  color: #fff;
}

.pagination {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.pagination-row {
  display: flex;
  gap: 10px;
  flex: 1;
  overflow: hidden;
  height: 30px;
  mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
}

.page-num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.page-num:hover {
  background: #e8e8e8;
}

.page-num.correct {
  background: #3d8c40;
  color: #fff;
}

.page-num.wrong {
  background: #e74c3c;
  color: #fff;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  padding: 40px 50px;
  min-width: 400px;
  text-align: center;
}

.modal-content {
  font-size: 18px;
  color: #333;
  margin-bottom: 35px;
  line-height: 1.6;
}

.modal-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.modal-btn {
  width: 140px;
  height: 45px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.confirm {
  background: #3d8c40;
  color: #fff;
  border: none;
}

.modal-btn.confirm:hover {
  background: #2e7d32;
}

.modal-btn.cancel {
  background: #fff;
  color: #3d8c40;
  border: 1px solid #3d8c40;
}

.modal-btn.cancel:hover {
  background: #f0f7f0;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .question-container {
    padding-top: 150px;
  }

  .main-wrapper {
    margin: 15px auto;
    padding: 0 10px;
  }

  .toolbar {
    padding: 15px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .title {
    order: -1;
    width: 100%;
    text-align: center;
    margin-bottom: 5px;
    position: static;
    transform: none;
  }

  .back-link {
    font-size: 13px;
  }

  .question-list {
    padding: 0 15px;
  }

  .question-item {
    padding: 20px 0;
  }

  .question-title {
    font-size: 14px;
  }

  .option {
    padding: 12px;
  }

  .option-text {
    font-size: 13px;
  }

  .answer-section {
    padding: 12px;
  }

  .footer-content {
    padding: 12px 15px;
    gap: 15px;
    flex-wrap: wrap;
  }

  .answer-stats {
    gap: 15px;
    flex-wrap: wrap;
  }

  .stat-item {
    font-size: 13px;
  }

  .answer-card-grid {
    padding: 0 15px 15px;
    gap: 8px;
    max-height: 150px;
  }

  .grid-num {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }

  .page-num {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .pagination-row {
    height: 26px;
  }

  .back-top {
    right: 15px;
    bottom: 80px;
  }

  .toolbar-right {
    gap: 8px;
  }

  .redo-btn {
    padding: 6px 16px;
    font-size: 13px;
  }

  .timer {
    padding: 6px 10px;
    font-size: 13px;
  }

  .modal-box {
    min-width: auto;
    width: 90%;
    padding: 30px 25px;
  }

  .modal-content {
    font-size: 16px;
    margin-bottom: 25px;
  }

  .modal-btn {
    width: 110px;
    height: 40px;
    font-size: 14px;
  }
}
</style>
